import { Check, Code, Copy, Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CoverageMentionSchema } from '@/services/types/coverage-types';

interface RawDataViewProps {
  mention: CoverageMentionSchema;
}

export const RawDataView = ({ mention }: RawDataViewProps) => {
  const [copied, setCopied] = useState(false);
  const [showPretty, setShowPretty] = useState(true);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(mention, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const formatObjectSize = (obj: any): string => {
    const jsonString = JSON.stringify(obj);
    const bytes = new Blob([jsonString]).size;

    if (bytes < 1024) return `${bytes} bytes`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getNestedObjectInfo = (
    obj: any,
    path = '',
  ): Array<{ path: string; type: string; value: any }> => {
    const info: Array<{ path: string; type: string; value: any }> = [];

    Object.entries(obj).forEach(([key, value]) => {
      const currentPath = path ? `${path}.${key}` : key;

      if (value === null) {
        info.push({ path: currentPath, type: 'null', value: null });
      } else if (Array.isArray(value)) {
        info.push({ path: currentPath, type: `array[${value.length}]`, value });
        if (value.length > 0 && typeof value[0] === 'object') {
          value.forEach((item, index) => {
            if (typeof item === 'object' && item !== null) {
              info.push(
                ...getNestedObjectInfo(item, `${currentPath}[${index}]`),
              );
            }
          });
        }
      } else if (typeof value === 'object') {
        info.push({ path: currentPath, type: 'object', value });
        info.push(...getNestedObjectInfo(value, currentPath));
      } else {
        info.push({ path: currentPath, type: typeof value, value });
      }
    });

    return info;
  };

  const objectInfo = getNestedObjectInfo(mention);
  const totalFields = objectInfo.length;
  const nonNullFields = objectInfo.filter((item) => item.value !== null).length;

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Code className="h-5 w-5" />
            Data Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Total Fields:</span>
              <span className="font-medium ml-2">{totalFields}</span>
            </div>
            <div>
              <span className="text-gray-600">Non-null Fields:</span>
              <span className="font-medium ml-2">{nonNullFields}</span>
            </div>
            <div>
              <span className="text-gray-600">Data Size:</span>
              <span className="font-medium ml-2">
                {formatObjectSize(mention)}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Completeness:</span>
              <span className="font-medium ml-2">
                {((nonNullFields / totalFields) * 100).toFixed(1)}%
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Field Explorer */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Field Explorer</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {objectInfo
              .filter(
                (item) => !item.path.includes('[') || item.path.includes('[0]'),
              ) // Show only first array item for brevity
              .slice(0, 50) // Limit to first 50 fields
              .map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between text-sm py-1 border-b border-gray-100"
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className="font-mono text-xs text-gray-600 truncate">
                      {item.path}
                    </span>
                    <Badge variant="outline" className="text-xs shrink-0">
                      {item.type}
                    </Badge>
                  </div>
                  <div className="text-xs text-gray-500 truncate max-w-32 ml-2">
                    {item.value === null
                      ? 'null'
                      : typeof item.value === 'string'
                        ? `"${item.value}"`
                        : typeof item.value === 'object'
                          ? '[object]'
                          : String(item.value)}
                  </div>
                </div>
              ))}
            {objectInfo.length > 50 && (
              <div className="text-center py-2 text-sm text-gray-500">
                ... and {objectInfo.length - 50} more fields
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Raw JSON */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Raw JSON Data</CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPretty(!showPretty)}
              className="gap-2"
            >
              {showPretty ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
              {showPretty ? 'Compact' : 'Pretty'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={copyToClipboard}
              className="gap-2"
            >
              {copied ? (
                <Check className="h-4 w-4 text-green-600" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
              {copied ? 'Copied!' : 'Copy'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-auto max-h-96">
            <pre className="text-xs leading-relaxed">
              {showPretty
                ? JSON.stringify(mention, null, 2)
                : JSON.stringify(mention)}
            </pre>
          </div>
        </CardContent>
      </Card>

      {/* Key Sections Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Section Completeness</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { key: 'extraction', label: 'Extraction Data' },
              { key: 'validation', label: 'Validation Data' },
              { key: 'search_queries', label: 'Search Queries' },
              { key: 'search_results', label: 'Search Results' },
              { key: 'candidate', label: 'Candidate Coverage' },
              { key: 'existing_entities', label: 'Existing Entities' },
              { key: 'integration', label: 'Integration Analysis' },
              { key: 'patch', label: 'Patch Data' },
              { key: 'feedback', label: 'Feedback' },
            ].map(({ key, label }) => {
              const value = mention[key as keyof CoverageMentionSchema];
              const hasData = value !== null && value !== undefined;
              const isEmpty =
                hasData &&
                ((Array.isArray(value) && value.length === 0) ||
                  (typeof value === 'object' &&
                    Object.keys(value).length === 0));

              return (
                <div
                  key={key}
                  className="flex items-center justify-between p-2 border rounded"
                >
                  <span className="text-sm font-medium">{label}</span>
                  <Badge
                    variant="outline"
                    className={
                      !hasData
                        ? 'bg-gray-100 text-gray-600'
                        : isEmpty
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-green-100 text-green-800'
                    }
                  >
                    {!hasData ? 'Missing' : isEmpty ? 'Empty' : 'Present'}
                  </Badge>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
